import { applyElementFormatting, EMPTY_FORMATTING, getFormattingKey } from "./formatting-state.mjs"

// Change detection compares text with the tags stripped, so making a word bold, adding a link or
// turning a paragraph into a heading would otherwise compare as unchanged. Like markListStructure,
// this stamps marker text into the HTML before the tags are stripped: "•bold•" where bold text
// starts, "•/•" where it goes back to plain. Markers only appear where the formatting changes
// between pieces of visible text, so b/strong swaps, runs split across tags and formatting on
// bare spaces all compare as unchanged.
//
// Works on the HTML string rather than a DOM so the server can share it.
const TOKEN_PATTERN = /<(\/?)([a-zA-Z][\w-]*)([^>]*)>|([^<]+)|</g
const VOID_ELEMENTS = new Set([
    "area",
    "base",
    "br",
    "col",
    "embed",
    "hr",
    "img",
    "input",
    "link",
    "meta",
    "source",
    "track",
    "wbr",
])
const STRUCTURE_STAMPS = { hr: "•hr•", tr: "•tr•", td: "•td•", th: "•td•" }
const BLANK_TEXT = /^(?:\s|&nbsp;|&#160;|&#xa0;|\u00a0)*$/i
const LEADING_BLANK_TEXT = /^(?:\s|&nbsp;|&#160;|&#xa0;|\u00a0)*/i
const ATTRIBUTE_PATTERNS = {
    href: /(?:^|\s)href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i,
    style: /(?:^|\s)style\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i,
}

/**
 * @param {string} html
 * @returns {string}
 */
export function markFormattingStructure(html) {
    if (!html) return html

    const stack = [{ tag: null, state: EMPTY_FORMATTING }]
    let lastKey = ""

    return `${html}`.replace(TOKEN_PATTERN, markToken)

    function markToken(match, closing, tagName, attributes, text) {
        if (text != null) return markText(text)
        if (tagName == null) return match

        const tag = tagName.toLowerCase()
        if (closing) {
            closeElement(tag)
            return match
        }

        if (!VOID_ELEMENTS.has(tag) && !/\/\s*$/.test(attributes)) {
            stack.push({ tag, state: applyElementFormatting(currentState(), tag, readAttribute) })
        }
        return `${match}${STRUCTURE_STAMPS[tag] ?? ""}`

        function readAttribute(name) {
            const found = ATTRIBUTE_PATTERNS[name]?.exec(attributes)
            return found ? found[1] ?? found[2] ?? found[3] : null
        }
    }

    function markText(text) {
        if (BLANK_TEXT.test(text)) return text

        // Case only counts inside a link: a link's text is often a document title, so a change to
        // its case is an edit, while case changes in the rest of the text are not
        const words = currentState().link ? keepCase(text) : text
        const key = getFormattingKey(currentState())
        if (key === lastKey) return words

        lastKey = key
        const marker = key ? `•${keepCase(key).replace(/</g, "&lt;")}•` : "•/•"
        return words.replace(LEADING_BLANK_TEXT, (blank) => `${blank}${marker}`)
    }

    function closeElement(tag) {
        for (let index = stack.length - 1; index > 0; index--) {
            if (stack[index].tag !== tag) continue
            stack.length = index
            return
        }
    }

    function currentState() {
        return stack[stack.length - 1].state
    }
}

// The normalised text is lowercased afterwards, which would make /Guide.pdf and /guide.pdf the
// same link. Those can point to different files, so capitals become ^ and the lower-case letter
// (with any ^ already there doubled) and survive the lowercasing. Entities such as &Eacute; are
// left as they are so they still decode.
function keepCase(value) {
    return value.replace(/&#?\w+;|\^|[A-Z]/g, (match) => {
        if (match.length > 1) return match
        return match === "^" ? "^^" : `^${match.toLowerCase()}`
    })
}
